import clsx from 'clsx'
import PropTypes from 'prop-types'
import Avatar from 'react-avatar'

import AuthAvatar from '../../../../UI/AuthAvatar'

import styles from './assignees.module.scss'

// Wie viele Personen einzeln stehen. Der Rest wird zu einer Zeile
// zusammengefasst — zwanzig Balken beantworten keine Frage mehr.
const MAX_ZEILEN = 8

/**
 * Wo die Arbeit liegt: offene Anforderungen je zuständiger Person.
 *
 * Waagrechte Balken, nicht noch ein Ring: Gefragt ist der Vergleich von
 * Größen über viele Kategorien, und Namen brauchen Platz zum Lesen. Ein Ring
 * mit zwölf Segmenten wäre hübsch und unlesbar.
 *
 * Sortiert nach Menge, längster oben — so steht die Antwort auf „wer hat am
 * meisten offen" in der ersten Zeile. „Niemandem zugewiesen" bleibt immer
 * sichtbar, auch bei wenigen Einträgen: liegengebliebene Arbeit ist genau das,
 * was man hier sucht.
 *
 * Jede Zeile filtert die Liste — wie die Stufen im Ring, mehrere gleichzeitig.
 */
export default function AssigneeBars({ counts, assignees, value, onChange }) {
	const active = value || []

	const personen = assignees.reduce((acc, person) => {
		acc[person.id] = person
		return acc
	}, {})

	const zeilen = Object.entries(counts || {})
		.map(([id, count]) => ({
			id: Number(id),
			// 0 ist keine echte Kennung, sondern „niemandem zugewiesen" — dieselbe
			// Konvention wie im Filter.
			name:
				Number(id) === 0 ? 'Niemandem zugewiesen' : personen[id]?.name || personen[id]?.email || `Konto ${id}`,
			// Dateiname des Profilbilds. Fehlt er, bleibt es bei den Initialen —
			// und bei „Niemandem zugewiesen" gibt es keinen Kopf, weil dort auch
			// keine Person steht.
			avatarHashName: Number(id) === 0 ? null : personen[id]?.avatarHashName || null,
			count: Number(count)
		}))
		.filter(zeile => zeile.count > 0)
		.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'de'))

	if (!zeilen.length) return null

	const sichtbar = zeilen.slice(0, MAX_ZEILEN)
	const rest = zeilen.slice(MAX_ZEILEN)
	const restSumme = rest.reduce((sum, zeile) => sum + zeile.count, 0)

	// Die Balkenlänge ist relativ zur größten Zeile — so ist der Vergleich auf
	// einen Blick zu machen, ohne dass man Zahlen liest.
	const max = Math.max(...zeilen.map(zeile => zeile.count))

	const toggle = id => {
		const next = active.includes(id) ? active.filter(item => item !== id) : [...active, id]
		onChange(next)
	}

	return (
		<div className={styles.root}>
			<h3 className={styles.titel}>Bei wem die Arbeit liegt</h3>
			<p className={styles.unterzeile}>
				Offene Anforderungen je zuständiger Person. Auf eine Zeile klicken, um die Liste darauf
				einzuschränken.
			</p>

			<ul className={styles.liste}>
				{sichtbar.map(zeile => {
					const isActive = active.includes(zeile.id)
					const gedimmt = active.length > 0 && !isActive

					return (
						<li key={zeile.id}>
							<button
								type="button"
								aria-pressed={isActive}
								className={clsx(
									styles.zeile,
									isActive && styles.zeileActive,
									gedimmt && styles.zeileDimmed
								)}
								onClick={() => toggle(zeile.id)}
							>
								{/*
								 * Der Kopf vor dem Namen. Acht Zeilen mit
								 * ähnlichen Nachnamen sind zu lesen, nicht zu
								 * erkennen; das Bild findet die eigene Zeile
								 * ohne Lesen. Fehlt das Bild, stehen die
								 * Initialen dort — dieselbe Vertretung wie in
								 * den Listen.
								 */}
								<span className={styles.kopf} aria-hidden="true">
									{zeile.id === 0 ? (
										<span className={styles.kopfLeer}>–</span>
									) : (
										<AuthAvatar
											hashName={zeile.avatarHashName}
											className={styles.kopfBild}
											fallback={<Avatar name={zeile.name} size="26" round />}
										/>
									)}
								</span>
								<span className={styles.name}>{zeile.name}</span>
								<span className={styles.balkenBahn}>
									<span
										className={clsx(
											styles.balken,
											zeile.id === 0 && styles.balkenOffen
										)}
										style={{ width: `${Math.max(4, (zeile.count / max) * 100)}%` }}
									/>
								</span>
								<span className={styles.zahl}>{zeile.count}</span>
							</button>
						</li>
					)
				})}
			</ul>

			{Boolean(rest.length) && (
				<p className={styles.rest}>
					… und {restSumme} bei {rest.length} weiteren Personen.
				</p>
			)}
		</div>
	)
}

AssigneeBars.propTypes = {
	// { "0": 3, "17": 5, … } — 0 steht für „niemandem zugewiesen".
	counts: PropTypes.object,
	// Für Namen und Profilbild. Fehlt eine Person (gelöschtes Konto), steht dort
	// die Kennung.
	assignees: PropTypes.array,
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired
}

AssigneeBars.defaultProps = {
	counts: {},
	assignees: [],
	value: []
}
